import { datadogRum } from '@datadog/browser-rum';
import { isRecord } from 'utils/typeGuards';
import { SessionStorageKey, sessionStore } from 'utils/typedStorage';

// Recovery for deploy-skew chunk failures: when a long-lived tab on an older
// build lazy-loads a content-hashed chunk that no longer exists (fetch failure)
// or no longer exports what the stale importer expects (link-time
// `doesn't provide an export named X`), the import rejects and the app breaks.
// We can't fix the stale tab in place, but a single reload pulls the current
// build's `index.html` (served `no-store`) and its matching chunk graph.

import {
  isChunkLoadErrorMessage,
  isCssPreloadErrorMessage,
  isDynamicImportFailureMessage,
} from './chunkLoadErrorMessage';
import { stringifyUnknownValue } from './stringifyUnknownValue';

// Session-scoped so the guard clears when the tab closes; a genuine (non-skew)
// bug can therefore reload at most once before degrading to a fallback. Only
// the persisted value counts: an in-memory guard would not survive the reload.
const RELOAD_GUARD_KEY = SessionStorageKey.ChunkReloadAttempted;

const extractMessage = (error: unknown): string => {
  if (typeof error === 'string') return error;
  if (error instanceof Error) return error.message;
  if (error && typeof error === 'object' && 'message' in error) {
    return stringifyUnknownValue(error.message);
  }
  return '';
};

/** True when `error` looks like a dynamic-import fetch/link failure. */
export const isChunkLoadError = (error: unknown): boolean => {
  const message = extractMessage(error);
  if (!message) return false;
  return isChunkLoadErrorMessage(message);
};

// Vite dispatches `vite:preloadError` for a failed stylesheet preload as well,
// with a message the dynamic-import matcher does not cover. It is the same
// deploy skew, but only the preload listener needs to know that: the error
// boundaries, `lazyWithReload`, and the app-entry fallback never see a
// stylesheet.

const DATADOG_PRELOAD_CHUNK_RE = /\/assets\/datadog-[^/?#\s]+\.js/i;
// Chunks fetched through loadNonEssentialModule: the lottie player
// (components/shared/LazyLottiePlayer) and the candidate form's phone validator
// (utils/phoneValidationAsync). The names come from the chunk groups in
// vite.config.ts; scripts/audit-chunks.mjs fails the build if they drift.
const HELD_IMPORT_CHUNK_RE =
  /\/assets\/(?:np-lottiefiles-|np-libphonenumber-js-|phoneUtils-)[^/?#\s]+\.js/i;
const ANY_CHUNK_URL_RE = /\/assets\/[^/?#\s]+\.js/i;

const extractErrorDetailText = (error: unknown): string =>
  [
    extractMessage(error),
    ...(() => {
      if (!isRecord(error)) {
        return [];
      }
      const { cause, stack } = error;
      return [
        ...(typeof stack === 'string' ? [stack] : []),
        ...(cause !== undefined ? [extractMessage(cause)] : []),
      ];
    })(),
  ].join('\n');

/** True when `error` is Vite's failed-stylesheet-preload error. */
const isCssPreloadError = (error: unknown): boolean =>
  isCssPreloadErrorMessage(extractMessage(error));

const isDatadogRecorderChunkFailure = (error: unknown): boolean =>
  DATADOG_PRELOAD_CHUNK_RE.test(extractErrorDetailText(error));

// Set once a reload has been scheduled in this page lifetime (reload() only
// schedules navigation; JS keeps running until it commits). Distinct from the
// sessionStorage guard, which persists across the reload — this resets on the
// new load. Lets a later handler in the same tick know a reload is already
// coming and avoid flashing a fallback before navigation.
let reloadInitiated = false;

/** True if a chunk-error reload has already been scheduled this page load. */
export const isReloadInitiated = (): boolean => reloadInitiated;

// Session replay's recorder is fetched after mount as its own chunk. Losing it
// costs telemetry only, so its failure must not reload the page the way a
// failed route chunk does. The SDK exposes no promise for that import, so the
// entry opens a bounded window around starting it. A route chunk that fails
// inside the window is still reloaded by lazyWithReload or the error boundary
// that receives its rejection; the preload listener is only the earlier path.
let nonEssentialImportWindowEnd = 0;

/** Treat module preload failures in the next `durationMs` as non-essential. */
export const openNonEssentialImportWindow = (durationMs: number): void => {
  nonEssentialImportWindowEnd = Date.now() + durationMs;
};

const inNonEssentialImportWindow = (): boolean =>
  Date.now() < nonEssentialImportWindowEnd;

// Non-essential imports the app starts itself (decoration, optional validators)
// hold the exemption for exactly as long as they are in flight, so a fetch that
// stalls past any fixed window is still covered.
let heldNonEssentialImports = 0;

/**
 * Run a dynamic import whose failure must never reload the page. The caller
 * still handles the rejection and supplies its own fallback.
 */
export const loadNonEssentialModule = <T>(
  load: () => Promise<T>
): Promise<T> => {
  heldNonEssentialImports += 1;
  const release = (): void => {
    heldNonEssentialImports -= 1;
  };
  const pending = load();
  pending.then(release, release);
  return pending;
};

// Warm-ups (utils/prefetchWhenIdle) fetch a chunk before anything needs it, so
// a failed one must not reload the page under the person. While one is in
// flight the listener leaves a failed module import to its importer: a lazy
// component that needs a chunk still reloads from lazyWithReload or its error
// boundary when its own import rejects. A failed stylesheet is put off until a
// lazy component needs it (see restoreWarmupStylesheets).
let heldWarmupImports = 0;

/** Run a warm-up dynamic import whose failure must not reload the page. */
export const loadWarmupModule = <T>(load: () => Promise<T>): Promise<T> => {
  heldWarmupImports += 1;
  const release = (): void => {
    heldWarmupImports -= 1;
  };
  const pending = load();
  pending.then(release, release);
  return pending;
};

// Vite's preload helper requests a stylesheet once per page and skips it on
// every later import of the chunk, so a stylesheet that failed during a warm-up
// is kept here and requested again by the next lazyWithReload import before it
// renders. Nothing at runtime maps a stylesheet to its chunk, so that import
// requests every stylesheet kept here. Each is also requested once more right
// away, without recovery, so a transient failure heals for importers that do
// not go through lazyWithReload too.
const deferredWarmupStylesheets = new Set<string>();
let restoringWarmupStylesheets: Promise<void> = Promise.resolve();
let requestingWarmupStylesheets = 0;

const CSS_PRELOAD_HREF_RE = /unable to preload css for (\S+)/i;

// Same element Vite's preload helper adds.
const appendStylesheet = (href: string): Promise<void> =>
  new Promise((resolve, reject) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.crossOrigin = '';
    link.href = href;
    link.addEventListener('load', () => resolve());
    link.addEventListener('error', () =>
      reject(new Error(`Unable to preload CSS for ${href}`))
    );
    document.head.appendChild(link);
  });

const deferWarmupStylesheet = (error: unknown): boolean => {
  const href = CSS_PRELOAD_HREF_RE.exec(extractMessage(error))?.[1];
  if (!href) return false;
  deferredWarmupStylesheets.add(href);
  appendStylesheet(href).then(
    () => {
      deferredWarmupStylesheets.delete(href);
    },
    () => {
      // swallow-ok: the stylesheet stays deferred; the next lazyWithReload import requests it again and recovers from a second failure
    }
  );
  return true;
};

// A stylesheet that fails again goes through the usual stylesheet recovery:
// one reload, else render without it.
const requestDeferredStylesheet = (href: string): Promise<void> => {
  requestingWarmupStylesheets += 1;
  return appendStylesheet(href)
    .catch((error: unknown) => {
      recoverFromStylesheetFailure(error);
    })
    .finally(() => {
      requestingWarmupStylesheets -= 1;
    });
};

/**
 * True while a stylesheet a warm-up failed to load is still to be requested
 * again or is loading. A component that renders an already loaded chunk's
 * module without lazyWithReload goes through lazyWithReload instead then.
 */
export const warmupStylesheetsPending = (): boolean =>
  deferredWarmupStylesheets.size > 0 || requestingWarmupStylesheets > 0;

/**
 * Requests again the stylesheets that failed while a warm-up held the reload,
 * and resolves once they have loaded or been recovered. lazyWithReload awaits
 * it before rendering, as Vite's helper would have awaited the stylesheet.
 */
export const restoreWarmupStylesheets = (): Promise<void> => {
  if (deferredWarmupStylesheets.size > 0) {
    const hrefs = [...deferredWarmupStylesheets];
    deferredWarmupStylesheets.clear();
    restoringWarmupStylesheets = Promise.all([
      restoringWarmupStylesheets,
      ...hrefs.map(requestDeferredStylesheet),
    ]).then(() => undefined);
  }
  return restoringWarmupStylesheets;
};

// Vite dispatches `vite:preloadError` before the import's own promise rejects,
// so the hold is still counted here. Safari omits the URL from the error; with
// a hold in flight a URL-less failure is treated as the held import. A route
// chunk that fails at the same moment is still reloaded by lazyWithReload or
// the error boundary that receives its rejection.
const isHeldNonEssentialImportFailure = (error: unknown): boolean => {
  if (heldNonEssentialImports === 0) return false;
  const detail = extractErrorDetailText(error);
  return HELD_IMPORT_CHUNK_RE.test(detail) || !ANY_CHUNK_URL_RE.test(detail);
};

// Feature boundaries that swallow an unrecovered chunk error must pin the
// guard so AppErrorBoundary's 10s re-arm cannot clear it and allow another
// reload for the same persistent failure.
let chunkReloadGuardHolds = 0;

/** Keep the one-shot reload guard until the holder unmounts or recovers. */
export const holdChunkReloadGuard = (): void => {
  chunkReloadGuardHolds += 1;
};

export const releaseChunkReloadGuard = (): void => {
  if (chunkReloadGuardHolds > 0) {
    chunkReloadGuardHolds -= 1;
  }
};

/**
 * Clear the reload guard after a successful mount so a later deploy in the same
 * session can recover again. A clean render means recovery worked or there was
 * no skew. No-ops while a feature boundary still holds the guard.
 */
export const clearChunkReloadGuard = (): void => {
  if (chunkReloadGuardHolds > 0) {
    return;
  }
  sessionStore.removePersisted(RELOAD_GUARD_KEY);
};

// A same-origin reload keeps this document alive, with its JS running, until
// the new response commits, so `pagehide` is the only reliable sign that the
// navigation happened. `beforeunload` fires even when the person chooses to stay.
const RELOAD_COMMIT_GRACE_MS = 10_000;

// A person who chose to stay goes on using the page; one who chose to leave
// waits for the new document. Interaction is what tells the two apart.
const INTERACTION_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart'];

let reloadCancelled = false;
let reloadCommitTimer: number | undefined;
let pendingReload: { error: unknown; context: string } | undefined;
let reloadPromptShown = false;
let interactedSincePrompt = false;
let reloadGraceElapsed = false;
const reloadCancelledListeners = new Set<() => void>();

/**
 * Run `callback` once if the scheduled chunk-error reload turns out to have
 * been cancelled (the page stayed behind a beforeunload prompt). Never fires
 * when no reload is pending or when the navigation commits. If the
 * cancellation was already detected, `callback` runs synchronously. Returns an
 * unsubscribe function.
 */
export const onChunkReloadCancelled = (callback: () => void): (() => void) => {
  if (reloadCancelled) {
    callback();
    return () => {};
  }
  reloadCancelledListeners.add(callback);
  return () => {
    reloadCancelledListeners.delete(callback);
  };
};

/** True once a chunk-error reload was cancelled in this page load. */
export const wasChunkReloadCancelled = (): boolean => reloadCancelled;

const persistReloadGuard = (): boolean =>
  sessionStore.setPersisted(RELOAD_GUARD_KEY, '1');

const stopWatchingReload = (): void => {
  window.clearTimeout(reloadCommitTimer);
  window.removeEventListener('pagehide', onReloadCommitting);
  window.removeEventListener('beforeunload', onReloadBeforeUnload);
  for (const type of INTERACTION_EVENTS) {
    window.removeEventListener(type, onReloadPageInteraction, true);
  }
};

function onReloadCommitting(): void {
  stopWatchingReload();
}

// Registered right before the reload, so it runs after the app's own
// beforeunload handlers and sees whether one of them asked for a prompt.
function onReloadBeforeUnload(event: BeforeUnloadEvent): void {
  reloadPromptShown = event.defaultPrevented || Boolean(event.returnValue);
}

function onReloadPageInteraction(): void {
  interactedSincePrompt = true;
  if (reloadGraceElapsed) {
    settleReloadCancellation();
  }
}

// Cancelled only with evidence on both counts: a prompt was shown, and the
// person kept using the page after the grace period. Without a prompt nobody
// could have chosen to stay, and without interaction the navigation may still
// be in flight on a slow connection.
const settleReloadCancellation = (): void => {
  if (!pendingReload || !reloadPromptShown || !interactedSincePrompt) {
    return;
  }
  markReloadCancelled(pendingReload.error, pendingReload.context);
};

const markReloadCancelled = (error: unknown, context: string): void => {
  stopWatchingReload();
  pendingReload = undefined;
  reloadInitiated = false;
  reloadCancelled = true;
  // The guard stays spent, and held against the app boundary's re-arm, so a
  // later chunk failure shows a fallback instead of prompting the user again.
  persistReloadGuard();
  holdChunkReloadGuard();
  datadogRum.addAction('chunk_load_reload_cancelled', {
    context,
    message: extractMessage(error),
  });
  const listeners = [...reloadCancelledListeners];
  reloadCancelledListeners.clear();
  for (const listener of listeners) {
    try {
      listener();
    } catch (listenerError) {
      // swallow-ok: Reload cancellation is recorded before listeners run; a failing observer must not prevent the remaining lazy-load boundaries from rendering their error fallback.
      console.error('Chunk reload cancellation listener failed', listenerError);
    }
  }
};

const watchForReloadCommit = (error: unknown, context: string): void => {
  pendingReload = { error, context };
  window.addEventListener('beforeunload', onReloadBeforeUnload, { once: true });
  window.addEventListener('pagehide', onReloadCommitting, { once: true });
  for (const type of INTERACTION_EVENTS) {
    window.addEventListener(type, onReloadPageInteraction, {
      capture: true,
      once: true,
      passive: true,
    });
  }
  reloadCommitTimer = window.setTimeout(() => {
    reloadGraceElapsed = true;
    settleReloadCancellation();
  }, RELOAD_COMMIT_GRACE_MS);
};

/**
 * Reload once to recover from a chunk failure. Returns true if a reload was
 * triggered; false if the per-session guard was already set (caller should
 * report the error and show a fallback instead of looping).
 */
export const reloadOnceForChunkError = (
  error: unknown,
  context: string
): boolean => {
  // One scheduled reload per page load: a later caller joins the pending one
  // through isReloadInitiated(), and nothing reloads automatically again after
  // the person chose to stay.
  if (reloadInitiated || reloadCancelled) {
    return false;
  }

  // Unreadable storage reads as "not attempted"; persistReloadGuard below then
  // decides whether a reload is safe.
  if (sessionStore.get(RELOAD_GUARD_KEY) !== null) {
    return false;
  }

  if (!persistReloadGuard()) {
    // The guard can't be persisted (e.g. Firefox strict tracking protection),
    // so a reload would repeat this exact path forever. Skip it and let the
    // caller show the fallback instead: recover manually, never loop.
    return false;
  }

  // Breadcrumb (not an error) so we can see recoveries without alert noise.
  datadogRum.addAction('chunk_load_auto_reload', {
    context,
    message: extractMessage(error),
  });

  watchForReloadCommit(error, context);
  reloadInitiated = true;
  window.location.reload();
  return true;
};

const asError = (error: unknown): Error => {
  if (error instanceof Error) return error;
  const message = extractMessage(error);
  return new Error(message || 'Chunk load failed');
};

// The same Error object can reach a catch and another listener. Identity keeps
// those as one report without collapsing two Safari failures that share the
// URL-less message "Importing a module script failed."
const reportedChunkObjects = new WeakSet<object>();
const reportedChunkMessages = new Set<string>();

const claimChunkFailure = (error: unknown): boolean => {
  if (typeof error === 'object' && error !== null) {
    if (reportedChunkObjects.has(error)) return false;
    reportedChunkObjects.add(error);
    return true;
  }
  const message = extractMessage(error);
  if (reportedChunkMessages.has(message)) return false;
  reportedChunkMessages.add(message);
  return true;
};

/**
 * Record a dynamic-import failure that this page will not reload away. A
 * reload already scheduled this page is the recovery signal (beforeSend drops
 * the browser copy); if that reload is cancelled, the report is sent then.
 * HTML-as-script is not reported here: the browser copy is kept on purpose.
 * Other errors are ignored so callers can pass a mixed catch. A repeated call
 * with the same error object is a no-op.
 */
export const reportUnrecoveredChunkError = (
  error: unknown,
  feature: string
): void => {
  if (!isDynamicImportFailureMessage(extractMessage(error))) return;
  if (!claimChunkFailure(error)) return;
  const report = (): void => {
    datadogRum.addError(asError(error), {
      feature,
      chunkReloadCancelled: wasChunkReloadCancelled(),
    });
  };
  if (isReloadInitiated()) {
    onChunkReloadCancelled(report);
    return;
  }
  report();
};

/**
 * Reload once for a stylesheet the page could not load. Suppressing the failure
 * is the end of the line for it: nothing downstream will ever see it, so report
 * it here or a session whose reload guard can never be set (private browsing,
 * strict tracking protection) renders unstyled for good with no trace.
 */
function recoverFromStylesheetFailure(error: unknown): void {
  const reloaded = reloadOnceForChunkError(error, 'vite_preload_css');
  // Only when no reload is coming at all. Vite calls the error handler once per
  // failed dependency, so a chunk missing several stylesheets reloads on the
  // first and finds the guard spent on the rest: reporting those would bury the
  // sessions that genuinely cannot recover under routine deploy skew.
  if (!reloaded && !isReloadInitiated()) {
    datadogRum.addError(error, { feature: 'css_preload_unrecovered' });
  } else if (reloaded) {
    // A cancelled reload leaves the page unstyled with no other trace. Only
    // the call that scheduled the reload subscribes, so one report per page.
    onChunkReloadCancelled(() => {
      datadogRum.addError(error, {
        feature: 'css_preload_unrecovered',
        chunkReloadCancelled: true,
      });
    });
  }
}

/** Breadcrumb context for a `vite:preloadError` worth recovering from. */
type PreloadErrorContext = 'vite_preload' | 'vite_preload_css';

/**
 * Classify a `vite:preloadError` payload, or null when the error is not deploy
 * skew and must be left alone.
 *
 * Only the stylesheet context is safe to suppress Vite's rethrow for. Vite
 * reports a failed stylesheet before it evaluates the module, so suppressing
 * there lets the module load unstyled instead of failing outright. On the
 * module branch the helper is `baseModule().catch(handlePreloadError)`, whose
 * result becomes the module namespace: suppressing resolves every
 * `const { x } = await import(...)` with `undefined`, which throws an unrelated
 * destructuring TypeError, hides the real cause, and denies `lazyWithReload`
 * the rejection it needs to hold Suspense until the reload commits.
 */
export const planPreloadErrorRecovery = (
  error: unknown
): PreloadErrorContext | null => {
  if (isCssPreloadError(error)) return 'vite_preload_css';
  if (isChunkLoadError(error)) return 'vite_preload';
  // An error thrown by the imported module itself (e.g. an unsupported API on
  // an old browser). A reload replays it, so leave the page alone.
  return null;
};

/**
 * Act on a `vite:preloadError` payload. Returns true when the listener should
 * call `preventDefault()`.
 */
export const recoverFromPreloadError = (error: unknown): boolean => {
  const context = planPreloadErrorRecovery(error);
  if (context === null) {
    return false;
  }

  if (context === 'vite_preload') {
    // Safari reports a recorder preload failure as "Importing a module script
    // failed" with no URL in `error.message`, but the failure URL often still
    // appears in `error.stack` or `error.cause` metadata.
    const recorderFailure =
      inNonEssentialImportWindow() && isDatadogRecorderChunkFailure(error);
    if (recorderFailure || isHeldNonEssentialImportFailure(error)) {
      // Reported here because the importer swallows the rejection: the SDK's
      // catch only logs to a console captured before RUM instrumented it, and
      // the held importers fall back silently.
      datadogRum.addError(error, {
        feature: 'non_essential_chunk_unrecovered',
      });
      return false;
    }
    if (heldWarmupImports > 0) {
      return false;
    }
    reloadOnceForChunkError(error, context);
    // Leave the rejection alone. A route or lazy component reports from its
    // error boundary, and a caught import reports from its own catch. Reporting
    // here as well would store the same failure twice.
    return false;
  }

  // Suppress the stylesheet failure even when no reload fired, because an
  // unstyled page still beats a failed import. A warm-up's import finishes
  // without the stylesheet, which the lazy component that renders the chunk
  // requests again first.
  if (heldWarmupImports === 0 || !deferWarmupStylesheet(error)) {
    recoverFromStylesheetFailure(error);
  }
  return true;
};
